import React, { useMemo, useContext } from 'react';
import WorkpageApi, { WorkpageListStubDTO } from '../api/WorkpageApi';
import LookupApi from '../api/LookupApi';
import Loader from '../components/Loader';
import useResourceLoader from '../util/useResourceLoader';
import { AuthContext } from './AuthContext';

const UWEContext: React.Context<UWEContextValue> & {
  Component: React.FunctionComponent<UWEContext_ComponentProps>
} = React.createContext<UWEContextValue>(undefined as unknown as UWEContextValue) as any;


export type TaggedWorkPageListStub = WorkpageListStubDTO & {
  default?: boolean
};

export type UWEContextValue = {
  workpages: TaggedWorkPageListStub[],
  adminWorkpages: TaggedWorkPageListStub[],
  hasAdminWorkpages?: boolean,
  appOptions: unknown,
};

export type UWEContext_ComponentProps = {
  children: React.ReactNode
};


UWEContext.Component = function UWEContext_Component(props: UWEContext_ComponentProps) {
  const { children } = props;
  const { value, loading } = useUWEContextComponentHooks(props);

  return <UWEContext.Provider value={value}>{loading !== false ? <Loader fullscreen spinner /> : children}</UWEContext.Provider>;
};

function useUWEContextComponentHooks({} : UWEContext_ComponentProps) {
  const auth = useContext(AuthContext);

  const [allWorkpages, loadingAllWorkpages, errorLoadingWorkpages] = useResourceLoader((ac) => {
    if (auth.user) {
      return WorkpageApi.list(undefined, ac);
    }

    return Promise.resolve([]);
  }, [auth]);

  const [
    workpages,
    adminWorkpages,
    hasAdminWorkpages,
  ] = useMemo(() => {
    const workpages: TaggedWorkPageListStub[] = allWorkpages?.filter(wp => !wp.isAdmin) || [];
    if (workpages?.length) {
      workpages[0].default = true;
    }

    const adminWorkpages = allWorkpages?.filter(wp => wp.isAdmin) || [];
    const hasAdminWorkpages = adminWorkpages.length > 0
    return [
      workpages,
      adminWorkpages,
      hasAdminWorkpages,
    ]
  }, [allWorkpages]);

  const [appOptions, loadingAppOptions, errorLoadingAppOptions] = useResourceLoader(async () => await LookupApi.getAppOptions(), []);

  const loading = loadingAllWorkpages || loadingAppOptions;
  const error = errorLoadingWorkpages || errorLoadingAppOptions;

  const value = useMemo<UWEContextValue>(
    () => ({
      workpages,
      adminWorkpages,
      hasAdminWorkpages,
      appOptions,
    }),
    [workpages, adminWorkpages, hasAdminWorkpages, appOptions]
  );

  return {
    value,
    loading,
    error,
  };
}

export default UWEContext;
